import Link from 'next/link'

import type { FC, ReactNode } from 'react'
import type { PostCard } from '@/types'

import { PillarChip } from '@/components/content/pillar-chip'
import { PostMeta } from '@/components/content/post-meta'

interface FeaturedArticleProps {
    post: PostCard
}

export const FeaturedArticle: FC<FeaturedArticleProps> = ({ post }): ReactNode => {
    const href = `/${post.pillarSlug}/${post.slug}`

    return (
        <article className='group grid overflow-hidden rounded-3xl border border-border bg-card lg:grid-cols-2'>
            <Link
                href={href}
                tabIndex={-1}
                aria-hidden='true'
                className='relative aspect-[16/11] overflow-hidden bg-muted lg:aspect-auto lg:min-h-[20rem]'
            >
                {post.coverImage && (
                    <img
                        src={post.coverImage}
                        alt=''
                        loading='lazy'
                        className='absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]'
                    />
                )}
            </Link>

            <div className='flex flex-col justify-center gap-4 p-7 sm:p-10'>
                <div className='flex items-center gap-3'>
                    <PillarChip
                        accent={post.accent}
                        label={post.pillarName}
                        href={`/${post.pillarSlug}`}
                        cmsRef={`pillars:${post.pillarId}:name`}
                    />
                    <span className='text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground'>
                        Featured
                    </span>
                </div>

                <h3 className='font-heading text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl'>
                    <Link
                        href={href}
                        data-mdlfy-cms={`posts:${post.id}:title`}
                        className='transition-colors hover:text-primary'
                    >
                        {post.title}
                    </Link>
                </h3>

                <p
                    data-mdlfy-cms={`posts:${post.id}:excerpt`}
                    className='line-clamp-3 text-base leading-relaxed text-muted-foreground'
                >
                    {post.excerpt}
                </p>

                <PostMeta
                    publishedAt={post.publishedAt}
                    readMinutes={post.readMinutes}
                />
            </div>
        </article>
    )
}
